/**
 * +grid: compiling Waystone Grid allocations into a ClassDef (docs/design/waystone-grid.md "Sim").
 * Pure and deterministic: the input is never mutated (a tree clone is patched) and the result depends
 * only on the ClassDef and the node ids.
 *
 *  - Minor stats sum into one hidden passive (`grid.<class>.stars`): maxHpPct, damageDonePct, hastePct…
 *    Resource regeneration patches the primary resource (regen and gain rules ×(1 + v)).
 *  - Slot modifiers are the "slot hook": the selector (hotbar key 1–7 or a role tag) is resolved
 *    against THIS ClassDef's binds, after the specialisation folded in, so a spec's evolved ability on
 *    key 2 gets the key-2 modifier. Ability-scoped stat mods go on a hidden passive; charges, extra
 *    targets and roots patch the ability. Transform forms (aura `overrides`) of the slot's ability are
 *    included. A modifier that can't apply (no cooldown to shorten, an instant to quicken) gives +5%
 *    damage to that ability instead.
 *  - Notables and keystones are hand-built auras (passives with procs, periodic "watchers" and
 *    visible buffs). Every aura id is namespaced by the class id, so two grid users in one sim (the
 *    hero and Group Finder strangers) never share a definition.
 *  - Idle stats: gather speed and out-of-combat run speed are sim auras; gold find, profession
 *    skill-ups and Auto's wits are read by progression / the bots (gridIdleTotals).
 */
import type { AbilityDef, AbilityId, AreaShape, AuraDef, ClassDef, Condition, EffectSpec, ProcSpec, StatMod, StatModKind } from '@keystone/sim';
import { GRID_IDLE_DORMANT, type GridIdle, type GridStat, type SlotMod, type SlotSelector, getGrid } from '@keystone/content';
import { forEachAura, treeClone } from './transforms';

/** Everything a set of nodes adds up to (unknown ids and dormant stars are ignored). */
export interface GridTotals {
  stats: Partial<Record<GridStat, number>>;
  idle: Partial<Record<GridIdle, number>>;
  slots: { sel: SlotSelector; mod: SlotMod }[];
  notables: string[];
  keystones: string[];
}

export function gridTotals(nodeIds: readonly string[]): GridTotals {
  const g = getGrid();
  const out: GridTotals = { stats: {}, idle: {}, slots: [], notables: [], keystones: [] };
  const seen = new Set<string>();
  for (const id of nodeIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    const n = g.byId.get(id);
    if (!n || n.type === 'dormant') continue;
    for (const e of n.effects) {
      if (e.kind === 'stat') out.stats[e.stat] = round4((out.stats[e.stat] ?? 0) + e.value);
      else if (e.kind === 'idle') out.idle[e.stat] = round4((out.idle[e.stat] ?? 0) + e.value);
      else if (e.kind === 'slot') out.slots.push({ sel: e.sel, mod: e.mod });
      else if (e.kind === 'notable') out.notables.push(e.id);
      else if (e.kind === 'keystone') out.keystones.push(e.id);
    }
  }
  return out;
}

/** Idle totals that something reads right now (Phase 2 stats such as offlineRate excluded). */
export function gridIdleTotals(nodeIds: readonly string[]): Partial<Record<GridIdle, number>> {
  const t = gridTotals(nodeIds).idle;
  for (const k of GRID_IDLE_DORMANT) delete t[k];
  return t;
}

const round4 = (v: number): number => Math.round(v * 10000) / 10000;

const STAT_MOD: Readonly<Record<Exclude<GridStat, 'regen'>, StatModKind>> = {
  maxHp: 'maxHpPct',
  damage: 'damageDonePct',
  healing: 'healingDonePct',
  haste: 'hastePct',
  crit: 'critPct',
  armor: 'armor',
  dodge: 'dodge',
  cooldown: 'cooldownRatePct',
  leech: 'leechPct',
  healingTaken: 'healingTakenPct',
  moveSpeed: 'moveSpeedPct',
};

/** Hotbar keys in display order (1–4 binds, 5–7 extraBinds). */
const SLOT_KEYS = ['1', '2', '3', '4', '5', '6', '7'] as const;

/** Ability bound on a hotbar key 1–7 (undefined when empty). */
function boundOn(def: ClassDef, key: (typeof SLOT_KEYS)[number]): AbilityId | undefined {
  return key === '5' || key === '6' || key === '7' ? def.extraBinds?.[key] : def.binds[key];
}

const TAG_ALIASES: Readonly<Record<string, readonly string[]>> = {
  defensive: ['defensive', 'majorDefensive', 'externalDefensive'],
  heal: ['heal', 'groupHeal'],
};

/** The slot hook: the ability a selector picks on this ClassDef (key, or first key 1–7 whose ability has the tag). */
export function slotAbility(def: ClassDef, sel: SlotSelector): AbilityDef | undefined {
  const byId = (id: AbilityId | undefined) => (id ? def.abilities.find((a) => a.id === id) : undefined);
  if ('key' in sel) return byId(boundOn(def, sel.key));
  const tags = TAG_ALIASES[sel.tag] ?? [sel.tag];
  for (const k of SLOT_KEYS) {
    const a = byId(boundOn(def, k));
    if (a?.ui?.tags?.some((t) => tags.includes(t))) return a;
  }
  return undefined;
}

/** A slot's ability id plus its transform forms (abilities an aura `overrides` it with). */
function slotIds(def: ClassDef, ab: AbilityDef): AbilityId[] {
  const ids = new Set<AbilityId>([ab.id]);
  forEachAura(def, (a) => {
    for (const o of a.overrides ?? []) if (o.abilityId === ab.id && def.abilities.some((x) => x.id === o.with)) ids.add(o.with);
  });
  return [...ids].sort();
}

/** Abilities on keys 1–7 (the hotbar kit), in key order. */
function kitAbilities(def: ClassDef): AbilityDef[] {
  const out: AbilityDef[] = [];
  for (const k of SLOT_KEYS) {
    const id = boundOn(def, k);
    const a = id ? def.abilities.find((x) => x.id === id) : undefined;
    if (a && !out.includes(a)) out.push(a);
  }
  return out;
}

const hasDirect = (a: AbilityDef, type: EffectSpec['type']): boolean => {
  const walk = (l: readonly EffectSpec[] | undefined): boolean => (l ?? []).some((e) => e.type === type || (e.type === 'conditional' && (walk(e.then) || walk(e.else))));
  return walk(a.effects) || walk(a.channelStartEffects);
};

// ---------------------------------------------------------------------------
// Aura helpers
// ---------------------------------------------------------------------------

function hiddenPassive(id: string, name: string, extra: Partial<AuraDef>): AuraDef {
  return { id, name, kind: 'buff', durationMs: 0, stacking: 'unique', flags: { hidden: true, persistThroughDeath: true }, ...extra };
}

function buff(id: string, name: string, durationMs: number, mods: StatMod[], extra: Partial<AuraDef> = {}): AuraDef {
  return { id, name, kind: 'buff', durationMs, stacking: 'refresh', mods, ...extra };
}

const toSelf = (aura: AuraDef, stacks?: number): EffectSpec => ({ type: 'applyAura', to: 'self', aura, ...(stacks ? { stacks } : {}) });
const healSelf = (pct: number): EffectSpec => ({ type: 'heal', to: 'self', amount: { pctSourceMaxHp: pct } });
const OUT_OF_COMBAT: Condition = { type: 'not', cond: { type: 'selfInCombat' } };
const when = (cond: Condition, then: EffectSpec[]): EffectSpec => ({ type: 'conditional', cond, then });

/** A hidden passive that checks `effects` every `intervalMs` (the "watcher" pattern). */
const watcher = (id: string, name: string, intervalMs: number, effects: EffectSpec[], extra: Partial<AuraDef> = {}): AuraDef => hiddenPassive(id, name, { periodic: { intervalMs, effects }, ...extra });

/** The class's primary resource and a share of its pool (at least 1). */
function primary(def: ClassDef): { id: string; share: (f: number) => number } | null {
  const r = def.resources[0];
  if (!r) return null;
  return { id: r.id, share: (f) => Math.max(1, Math.round(r.max * f)) };
}

const costly = (def: ClassDef): AbilityDef[] => def.abilities.filter((a) => (a.cost ?? []).some((c) => c.amount > 0));

// ---------------------------------------------------------------------------
// Notables and keystones
// ---------------------------------------------------------------------------

interface Built {
  /** Mods merged into the stars aura (plain stat notables). */
  mods?: StatMod[];
  /** Passive auras to add. */
  passives?: AuraDef[];
}

type Builder = (def: ClassDef, ns: string) => Built;

const slotIdsOf = (def: ClassDef, sel: SlotSelector): AbilityId[] => {
  const a = slotAbility(def, sel);
  return a ? slotIds(def, a) : [];
};

/** A proc on casts of the slot's ability (nothing when the slot is empty). */
function onSlot(def: ClassDef, sel: SlotSelector, p: Omit<ProcSpec, 'abilityIds'>): ProcSpec[] {
  const ids = slotIdsOf(def, sel);
  return ids.length ? [{ ...p, abilityIds: ids }] : [];
}

const NOTABLES: Readonly<Record<string, Builder>> = {
  grindstone: (_d, ns) => ({ passives: [hiddenPassive(`${ns}.grindstone`, 'Grindstone', { procs: [{ on: 'killCredit', effects: [healSelf(0.03)] }] })] }),
  second_breath: (def, ns) => {
    const ids = costly(def).map((a) => a.id);
    if (!ids.length) return {};
    const b = buff(`${ns}.second_breath`, 'Second Breath', 0, [{ stat: 'resourceCostPct', value: -1, abilityIds: ids }], {
      stacking: 'ignore',
      procs: [{ on: 'castResolved', abilityIds: ids, consumeStack: true, effects: [] }],
    });
    return { passives: [watcher(`${ns}.second_breath_watch`, 'Second Breath', 1000, [when(OUT_OF_COMBAT, [toSelf(b)])])] };
  },
  anvil_heart: (_d, ns) => {
    const b = buff(`${ns}.anvil_heart`, 'Anvil Heart', 1000, [{ stat: 'damageTakenPct', value: -0.12 }]);
    return { passives: [watcher(`${ns}.anvil_heart_watch`, 'Anvil Heart', 500, [when({ type: 'selfHpBelow', pct: 0.5 }, [toSelf(b)])])] };
  },
  unbroken_line: (_d, ns) => ({
    passives: [hiddenPassive(`${ns}.unbroken_line`, 'Unbroken Line', { flags: { hidden: true, persistThroughDeath: true, cheatDeath: true }, onCheatDeath: [healSelf(0.25)] })],
  }),
  hooves_of_war: (def, ns) => {
    const b = buff(`${ns}.hooves_of_war`, 'Hooves of War', 6000, [{ stat: 'damageDonePct', value: 0.08 }]);
    const procs = onSlot(def, { key: '2' }, { on: 'castResolved', effects: [toSelf(b)] });
    return procs.length ? { passives: [hiddenPassive(`${ns}.hooves_of_war_p`, 'Hooves of War', { procs })] } : {};
  },
  pack_hunter: (_d, ns) => {
    // Three tiers (one per nearby enemy), each refreshed while it holds: no remove/re-add churn.
    const tier = (i: number): AuraDef =>
      buff(`${ns}.pack_hunter${i}`, 'Pack Hunter', 1500, [{ stat: 'damageDonePct', value: 0.04 }], i > 1 ? { flags: { hidden: true } } : {});
    const effects = [1, 2, 3].map((i) => when({ type: 'enemiesNearSelf', radius: 10, atLeast: i }, [toSelf(tier(i))]));
    return { passives: [watcher(`${ns}.pack_hunter_watch`, 'Pack Hunter', 1000, effects)] };
  },
  knifes_edge: (def, ns) => {
    const r = primary(def);
    if (!r) return {};
    return { passives: [hiddenPassive(`${ns}.knifes_edge`, "Knife's Edge", { procs: [{ on: 'critDealt', icdMs: 1000, effects: [{ type: 'resource', to: 'self', resource: r.id, amount: r.share(0.02) }] }] })] };
  },
  quickstep: (_d, ns) => {
    const b = buff(`${ns}.quickstep`, 'Quickstep', 4000, [{ stat: 'hastePct', value: 0.08 }]);
    return { passives: [hiddenPassive(`${ns}.quickstep_p`, 'Quickstep', { procs: [{ on: 'avoided', effects: [toSelf(b)] }] })] };
  },
  ghost_step: (_d, ns) => {
    const b = buff(`${ns}.ghost_step`, 'Ghost Step', 5000, [
      { stat: 'moveSpeedPct', value: 0.3 },
      { stat: 'damageDonePct', value: 0.06 },
    ]);
    return { passives: [hiddenPassive(`${ns}.ghost_step_p`, 'Ghost Step', { procs: [{ on: 'killCredit', effects: [toSelf(b)] }] })] };
  },
  patient_blade: (def, ns) => {
    const ids = kitAbilities(def).filter((a) => hasDirect(a, 'damage')).map((a) => a.id);
    if (!ids.length) return {};
    const b = buff(`${ns}.patient_blade`, 'Patient Blade', 0, [{ stat: 'critChanceBonus', value: 1, abilityIds: ids }], {
      stacking: 'ignore',
      procs: [{ on: 'castResolved', abilityIds: ids, consumeStack: true, effects: [] }],
    });
    return { passives: [watcher(`${ns}.patient_blade_watch`, 'Patient Blade', 1000, [when(OUT_OF_COMBAT, [toSelf(b)])])] };
  },
  quarry: (def, ns) => {
    const mark: AuraDef = { id: `${ns}.quarry`, name: 'Quarry', kind: 'debuff', durationMs: 6000, stacking: 'refresh', mods: [{ stat: 'damageTakenPct', value: 0.05 }] };
    const procs = onSlot(def, { key: '2' }, { on: 'castResolved', effects: [{ type: 'applyAura', aura: mark, if: { type: 'targetIsEnemy' } }] });
    return procs.length ? { passives: [hiddenPassive(`${ns}.quarry_p`, 'Quarry', { procs })] } : {};
  },
  fletchers_rhythm: (def, ns) => {
    const k1 = slotIdsOf(def, { key: '1' });
    const k2 = slotIdsOf(def, { key: '2' });
    if (!k1.length || !k2.length) return {};
    const count = buff(`${ns}.fletchers_count`, "Fletcher's Count", 30000, [], { stacking: 'stack', maxStacks: 4, flags: { hidden: true } });
    const ready = buff(`${ns}.fletchers_rhythm`, "Fletcher's Rhythm", 15000, [{ stat: 'resourceCostPct', value: -1, abilityIds: k2 }], {
      procs: [{ on: 'castResolved', abilityIds: k2, consumeStack: true, effects: [] }],
    });
    const effects: EffectSpec[] = [toSelf(count), when({ type: 'selfHasAura', auraId: count.id, minStacks: 4 }, [{ type: 'removeAura', to: 'self', auraId: count.id }, toSelf(ready)])];
    return { passives: [hiddenPassive(`${ns}.fletchers_p`, "Fletcher's Rhythm", { procs: [{ on: 'castResolved', abilityIds: k1, effects }] })] };
  },
  wind_at_back: () => ({ mods: [{ stat: 'moveSpeedPct', value: 0.1 }, { stat: 'hastePct', value: 0.02 }] }),
  twin_fletch: (_d, ns) => ({
    passives: [hiddenPassive(`${ns}.twin_fletch`, 'Twin Fletch', { procs: [{ on: 'critDealt', chance: 0.2, icdMs: 500, effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.3 }, avoidable: false, if: { type: 'targetIsEnemy' } }] }] })],
  }),
  long_hunt: (_d, ns) => {
    const b = buff(`${ns}.long_hunt`, 'Long Hunt', 10000, [{ stat: 'hastePct', value: 0.03 }], { stacking: 'stack', maxStacks: 3 });
    return { passives: [hiddenPassive(`${ns}.long_hunt_p`, 'Long Hunt', { procs: [{ on: 'killCredit', effects: [toSelf(b)] }] })] };
  },
  spellweave: (def, ns) => {
    const aoe = slotAbility(def, { tag: 'aoe' });
    if (!aoe?.cooldown) return {};
    const procs = onSlot(def, { key: '1' }, { on: 'castResolved', effects: [{ type: 'modifyCooldown', abilityId: aoe.id, deltaMs: -1000 }] });
    return procs.length ? { passives: [hiddenPassive(`${ns}.spellweave`, 'Spellweave', { procs })] } : {};
  },
  static_mind: (_d, ns) => {
    const b = buff(`${ns}.static_mind`, 'Static Mind', 6000, [{ stat: 'hastePct', value: 0.01 }], { stacking: 'stack', maxStacks: 5 });
    return { passives: [hiddenPassive(`${ns}.static_mind_p`, 'Static Mind', { procs: [{ on: 'critDealt', effects: [toSelf(b)] }] })] };
  },
  glass_clarity: (_d, ns) => {
    const b = buff(`${ns}.glass_clarity`, 'Glass Clarity', 1000, [{ stat: 'damageDonePct', value: 0.06 }]);
    return { passives: [watcher(`${ns}.glass_clarity_watch`, 'Glass Clarity', 500, [when({ type: 'not', cond: { type: 'selfHpBelow', pct: 0.8 } }, [toSelf(b)])])] };
  },
  afterglow: (def, ns) => {
    const procs = onSlot(def, { key: '2' }, { on: 'castResolved', effects: [{ type: 'damage', school: 'arcane', amount: { coef: 0.25 }, avoidable: false, if: { type: 'targetIsEnemy' } }] });
    return procs.length ? { passives: [hiddenPassive(`${ns}.afterglow`, 'Afterglow', { procs })] } : {};
  },
  deep_font: (def, ns) => {
    const r = primary(def);
    if (!r) return {};
    return { passives: [watcher(`${ns}.deep_font`, 'Deep Font', 10000, [{ type: 'resource', to: 'self', resource: r.id, amount: r.share(0.05) }])] };
  },
  kindred_light: (_d, ns) => ({ passives: [hiddenPassive(`${ns}.kindred_light`, 'Kindred Light', { procs: [{ on: 'healDealt', icdMs: 1000, effects: [healSelf(0.02)] }] })] }),
  hearth_pulse: (_d, ns) => ({
    passives: [
      watcher(`${ns}.hearth_pulse`, 'Hearth Pulse', 6000, [
        { type: 'heal', to: 'area', area: { anchor: 'caster', shape: { type: 'circle', radius: 10 }, affects: 'allies' }, amount: { pctSourceMaxHp: 0.015 }, if: { type: 'selfInCombat' } },
      ]),
    ],
  }),
  rekindle: (_d, ns) => ({ passives: [hiddenPassive(`${ns}.rekindle`, 'Rekindle', { procs: [{ on: 'damageTaken', icdMs: 60000, if: { type: 'selfHpBelow', pct: 0.3 }, effects: [healSelf(0.2)] }] })] }),
  mercy_tide: (def, ns) => {
    const b = buff(`${ns}.mercy_tide`, 'Mercy Tide', 5000, [{ stat: 'damageTakenPct', value: -0.06 }]);
    const procs = onSlot(def, { key: '3' }, { on: 'castResolved', effects: [{ type: 'conditional', cond: { type: 'targetIsEnemy' }, then: [toSelf(b)], else: [{ type: 'applyAura', aura: b }] }] });
    return procs.length ? { passives: [hiddenPassive(`${ns}.mercy_tide_p`, 'Mercy Tide', { procs })] } : {};
  },
  candlewake: (def, ns) => {
    const ids = kitAbilities(def).map((a) => a.id);
    if (!ids.length) return {};
    const count = buff(`${ns}.candle_count`, 'Candlewake Count', 30000, [], { stacking: 'stack', maxStacks: 4, flags: { hidden: true } });
    const b = buff(`${ns}.candlewake`, 'Candlewake', 6000, [
      { stat: 'damageDonePct', value: 0.06 },
      { stat: 'healingDonePct', value: 0.06 },
    ]);
    const effects: EffectSpec[] = [toSelf(count), when({ type: 'selfHasAura', auraId: count.id, minStacks: 4 }, [{ type: 'removeAura', to: 'self', auraId: count.id }, toSelf(b)])];
    return { passives: [hiddenPassive(`${ns}.candlewake_p`, 'Candlewake', { procs: [{ on: 'castResolved', abilityIds: ids, effects }] })] };
  },
  red_tempo: (_d, ns) => {
    const b = buff(`${ns}.red_tempo`, 'Red Tempo', 5000, [{ stat: 'hastePct', value: 0.06 }]);
    return { passives: [hiddenPassive(`${ns}.red_tempo_p`, 'Red Tempo', { procs: [{ on: 'meleeSwingLanded', chance: 0.2, effects: [toSelf(b)] }] })] };
  },
  foxs_slip: (def, ns) => {
    const b = buff(`${ns}.foxs_slip`, "Fox's Slip", 4000, [{ stat: 'damageDonePct', value: 0.08 }]);
    const procs = onSlot(def, { tag: 'movement' }, { on: 'castResolved', effects: [toSelf(b)] });
    return procs.length ? { passives: [hiddenPassive(`${ns}.foxs_slip_p`, "Fox's Slip", { procs })] } : {};
  },
  clear_sight: (def, ns) => {
    const r = primary(def);
    return {
      mods: [{ stat: 'critPct', value: 0.03 }],
      ...(r ? { passives: [hiddenPassive(`${ns}.clear_sight`, 'Clear Sight', { procs: [{ on: 'critDealt', icdMs: 500, effects: [{ type: 'resource', to: 'self', resource: r.id, amount: r.share(0.01) }] }] })] } : {}),
    };
  },
  starlit_mind: () => ({ mods: [{ stat: 'hastePct', value: 0.03 }, { stat: 'healingDonePct', value: 0.04 }] }),
  bulwark_psalm: () => ({ mods: [{ stat: 'maxHpPct', value: 0.05 }, { stat: 'healingTakenPct', value: 0.06 }] }),
};

const KEYSTONES: Readonly<Record<string, Builder>> = {
  // Separate auras: their mods multiply with the stars' (−20% of the whole pool, not of the base).
  glass_vow: (_d, ns) => ({
    passives: [hiddenPassive(`${ns}.glass_vow`, 'Glass Vow', { mods: [{ stat: 'damageDonePct', value: 0.25 }, { stat: 'healingDonePct', value: 0.25 }, { stat: 'maxHpPct', value: -0.2 }] })],
  }),
  iron_root: (_d, ns) => ({
    passives: [hiddenPassive(`${ns}.iron_root`, 'Iron Root', { mods: [{ stat: 'damageTakenPct', value: -0.15 }, { stat: 'critPct', value: -5 }, { stat: 'critChanceBonus', value: -5 }] })],
  }),
  blood_price: (def, ns) => {
    const procs: ProcSpec[] = [];
    for (const a of costly(def)) {
      let pct = 0;
      for (const c of a.cost ?? []) {
        const pool = def.resources.find((r) => r.id === c.resource);
        if (pool && pool.max > 0 && c.amount > 0) pct += 0.12 * (c.amount / pool.max);
      }
      pct = Math.min(0.1, round4(pct));
      if (pct > 0) procs.push({ on: 'cast', abilityIds: [a.id], effects: [{ type: 'damage', to: 'self', school: 'physical', amount: { pctSourceMaxHp: pct }, selfHarm: true }] });
    }
    return { passives: [hiddenPassive(`${ns}.blood_price`, 'Blood Price', { mods: [{ stat: 'resourceCostPct', value: -1 }], ...(procs.length ? { procs } : {}) })] };
  },
};

/** Ids every notable / keystone builder covers (tests: content and mechanics agree). */
export const GRID_NOTABLE_IMPLS: readonly string[] = Object.keys(NOTABLES);
export const GRID_KEYSTONE_IMPLS: readonly string[] = Object.keys(KEYSTONES);

// ---------------------------------------------------------------------------
// Slot modifiers
// ---------------------------------------------------------------------------

const ROOT_NAME = 'Star-Bound';

/** Apply one slot modifier to `def` (in place; `mods` collects ability-scoped stat mods). Returns whether it applied as written. */
function applySlot(def: ClassDef, ns: string, sel: SlotSelector, mod: SlotMod, mods: StatMod[]): boolean {
  const ab = slotAbility(def, sel);
  if (!ab) return false;
  const ids = slotIds(def, ab);
  const fallback = (): false => {
    mods.push({ stat: 'damageDonePct', value: 0.05, abilityIds: ids }, { stat: 'healingDonePct', value: 0.05, abilityIds: ids });
    return false;
  };
  switch (mod.kind) {
    case 'damage':
      mods.push({ stat: 'damageDonePct', value: mod.pct, abilityIds: ids }, { stat: 'healingDonePct', value: mod.pct, abilityIds: ids });
      return true;
    case 'cooldown':
      if (!ab.cooldown) return fallback();
      mods.push({ stat: 'cooldownRatePct', value: mod.pct, abilityIds: ids });
      return true;
    case 'cost':
      if (!(ab.cost ?? []).some((c) => c.amount > 0)) return fallback();
      mods.push({ stat: 'resourceCostPct', value: -mod.pct, abilityIds: ids });
      return true;
    case 'crit':
      mods.push({ stat: 'critChanceBonus', value: mod.pct, abilityIds: ids });
      return true;
    case 'castSpeed':
      if (ab.cast?.type !== 'cast') return fallback();
      mods.push({ stat: 'castTimePct', value: -mod.pct, abilityIds: ids });
      return true;
    case 'charge': {
      let hit = false;
      for (const id of ids) {
        const a = def.abilities.find((x) => x.id === id);
        if (!a?.cooldown) continue;
        a.cooldown = { ...a.cooldown, charges: (a.cooldown.charges ?? 1) + 1 };
        hit = true;
      }
      return hit || fallback();
    }
    case 'extraTarget': {
      let capped = false;
      let area = false;
      const visit = (l: EffectSpec[] | undefined, apply: (e: EffectSpec) => void): void => {
        for (const e of l ?? []) {
          apply(e);
          if (e.type === 'conditional') {
            visit(e.then, apply);
            visit(e.else, apply);
          }
          if (e.type === 'telegraph') visit(e.effects, apply);
          if (e.type === 'zone' && e.tick) visit(e.tick.effects, apply);
        }
      };
      const lists = (a: AbilityDef): (EffectSpec[] | undefined)[] => [a.effects, a.channelStartEffects, a.channelEndEffects];
      for (const id of ids) {
        const a = def.abilities.find((x) => x.id === id)!;
        for (const l of lists(a))
          visit(l, (e) => {
            if (e.area?.maxTargets !== undefined) {
              e.area.maxTargets += mod.n;
              capped = true;
            }
            if (e.area || e.type === 'zone' || e.type === 'telegraph') area = true;
          });
      }
      if (capped) return true;
      if (!area) return fallback();
      // Uncapped areas hit everything inside already: widen them instead (+15% radius).
      for (const id of ids) {
        const a = def.abilities.find((x) => x.id === id)!;
        for (const l of lists(a))
          visit(l, (e) => {
            if (e.area) widen(e.area.shape);
            if (e.type === 'zone' || e.type === 'telegraph') widen(e.shape);
          });
      }
      return true;
    }
    case 'root': {
      const root: AuraDef = { id: `${ns}.root`, name: ROOT_NAME, kind: 'debuff', durationMs: mod.ms, stacking: 'refresh', cc: 'root' };
      let hit = false;
      for (const id of ids) {
        const a = def.abilities.find((x) => x.id === id)!;
        if (a.targeting === 'enemy') {
          a.effects.push({ type: 'applyAura', aura: root, if: { type: 'targetIsEnemy' } });
          hit = true;
          continue;
        }
        const dmg = a.effects.find((e) => e.type === 'damage' && e.to === 'area' && e.area && e.area.affects === 'enemies');
        if (dmg?.area) {
          a.effects.push({ type: 'applyAura', to: 'area', area: treeClone(dmg.area), aura: root });
          hit = true;
        }
      }
      return hit || fallback();
    }
  }
}

function widen(s: AreaShape): void {
  const m = 1.15;
  if (s.type === 'circle') s.radius = round4(s.radius * m);
  else if (s.type === 'cone') s.range = round4(s.range * m);
  else if (s.type === 'ring') s.outer = round4(s.outer * m);
  else if (s.type === 'line') s.length = round4(s.length * m);
  else {
    s.w = round4(s.w * m);
    s.h = round4(s.h * m);
  }
}

// ---------------------------------------------------------------------------
// The compile step
// ---------------------------------------------------------------------------

export interface GridCompileInfo {
  /** Slot modifiers that fell back to +5% damage (selector found nothing usable). */
  fallbacks: number;
  /** Slot modifiers whose slot is empty on this class (no ability on that key / with that tag). */
  empty: number;
}

/**
 * Fold a grid allocation into a ClassDef (pure). The base def is returned untouched when no node
 * has an effect. `info` (optional) reports slot modifiers that could not apply as written.
 */
export function applyGrid(def: ClassDef, nodeIds: readonly string[], info?: GridCompileInfo): ClassDef {
  const t = gridTotals(nodeIds);
  const anyIdle = Object.keys(t.idle).some((k) => !GRID_IDLE_DORMANT.includes(k as GridIdle));
  if (!Object.keys(t.stats).length && !anyIdle && !t.slots.length && !t.notables.length && !t.keystones.length) return def;
  const out = treeClone(def);
  const ns = `grid.${def.id}`;
  const passives: AuraDef[] = [];
  const mods: StatMod[] = [];

  // Stars: one hidden passive with every summed stat.
  for (const [stat, v] of Object.entries(t.stats) as [GridStat, number][]) {
    if (!v) continue;
    if (stat === 'regen') {
      const r = out.resources[0];
      if (r) {
        const m = 1 + v;
        if (r.regenPerSec !== undefined && r.regenPerSec > 0) r.regenPerSec = round4(r.regenPerSec * m);
        if (r.regenPerSecOoc !== undefined && r.regenPerSecOoc > 0) r.regenPerSecOoc = round4(r.regenPerSecOoc * m);
        for (const g of r.gainOn ?? []) if (g.amount > 0) g.amount = round4(g.amount * m);
      }
      continue;
    }
    mods.push({ stat: STAT_MOD[stat], value: v });
  }
  if (t.idle.gatherSpeed) mods.push({ stat: 'gatherSpeedPct', value: t.idle.gatherSpeed });
  if (t.idle.oocSpeed) {
    const stride = buff(`${ns}.wayfarer`, "Wayfarer's Stride", 0, [{ stat: 'moveSpeedPct', value: t.idle.oocSpeed }], { stacking: 'ignore', flags: { removeInCombat: true, hidden: true } });
    passives.push(watcher(`${ns}.wayfarer_watch`, "Wayfarer's Stride", 1000, [when(OUT_OF_COMBAT, [toSelf(stride)])]));
  }

  // Slot modifiers (the slot hook).
  let fallbacks = 0;
  let empty = 0;
  for (const s of t.slots) {
    if (!slotAbility(out, s.sel)) {
      empty++;
      continue;
    }
    if (!applySlot(out, ns, s.sel, s.mod, mods)) fallbacks++;
  }
  if (info) {
    info.fallbacks = fallbacks;
    info.empty = empty;
  }

  for (const id of t.notables) {
    const b = NOTABLES[id]?.(out, ns);
    if (b?.mods) mods.push(...b.mods);
    if (b?.passives) passives.push(...b.passives);
  }
  for (const id of t.keystones) {
    const b = KEYSTONES[id]?.(out, ns);
    if (b?.mods) mods.push(...b.mods);
    if (b?.passives) passives.push(...b.passives);
  }

  if (mods.length) passives.unshift(hiddenPassive(`${ns}.stars`, 'Waystone Grid', { mods: mergeMods(mods) }));
  out.passives = [...(out.passives ?? []), ...passives];
  return out;
}

/** Sum mods with the same stat and ability scope (keeps the aura small; order is deterministic). */
function mergeMods(mods: readonly StatMod[]): StatMod[] {
  const out: StatMod[] = [];
  const key = (m: StatMod): string => `${m.stat}|${(m.abilityIds ?? []).join(',')}|${m.school ?? ''}`;
  const at = new Map<string, StatMod>();
  for (const m of mods) {
    const k = key(m);
    const prev = at.get(k);
    if (prev) prev.value = round4(prev.value + m.value);
    else {
      const c: StatMod = { ...m, ...(m.abilityIds ? { abilityIds: [...m.abilityIds] } : {}) };
      at.set(k, c);
      out.push(c);
    }
  }
  return out;
}
